import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import {
  MANAGED_TABLE_CONFIGS,
  type ManagedTableColumn,
  type ManagedTableConfig,
  type ManagedTableEditableField,
  type ManagedTableKey,
  type ManagedTableRecord,
} from '@oneup/shared';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  DetailFact,
  DetailFactGrid,
  DetailGroup,
  DetailRow,
  DetailsDrawerError,
  DetailsDrawerSkeleton,
  DetailsPanelBody,
  JsonPreview,
} from '@/components/details-drawer';
import {
  ManagementCardGrid,
  ManagementEmptyState,
  ManagementErrorState,
  ManagementFooterSummary,
  ManagementHeader,
  ManagementLoadingState,
  ManagementPage,
  ManagementRecordCard,
  ManagementStatusBadge,
  ManagementTable,
  type ManagementActiveFilter,
  type ManagementFilterOption,
} from '@/components/management';
import { managedTablesApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  CompactListMediaFallback,
  CompactRecordList,
  CompactRecordListItem,
  RecordDetailHeading,
  RecordDetailShell,
  WorkspaceSplitView,
} from '@/components/workspace';
import {
  contextItem,
  pageContext,
  suggestion,
  useAlicePageContext,
  type AliceContextSourceType,
} from '@/components/alice';
import { adminConfig } from '@/platform/config';
import { MANAGEMENT_PAGES, usePersistentManagementView } from '@/platform/management-pages';
import { useUiStore } from '@/stores/ui';

interface ManagedTableViewProps {
  tableKey: ManagedTableKey;
}

export function ManagedTableView({ tableKey }: ManagedTableViewProps) {
  const navigate = useNavigate();
  const { recordId } = useParams<{ recordId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const config = MANAGED_TABLE_CONFIGS[tableKey];
  const page = MANAGEMENT_PAGES[tableKey];
  const search = searchParams.get('search') ?? '';
  const status = searchParams.get('status') ?? 'All';
  const [viewMode, setViewMode] = usePersistentManagementView(
    `managed-table:${tableKey}`,
    searchParams,
    setSearchParams,
  );

  const query = useQuery({
    queryKey: ['managed-table', tableKey, { search, status }],
    queryFn: () =>
      managedTablesApi.list(tableKey, {
        search: search || undefined,
        status: status !== 'All' ? status : undefined,
        limit: 300,
      }),
  });

  const records = query.data?.items ?? [];
  const selectedRecord = recordId ? records.find((record) => record.id === recordId) : undefined;
  const selectedIndex = recordId ? records.findIndex((record) => record.id === recordId) : -1;
  const statusOptions = useMemo(
    () => buildStatusOptions(records, config, status),
    [config, records, status],
  );
  const activeFilters = useMemo(
    () =>
      buildActiveFilters({
        search,
        status,
        setSearch: (value) => setParam(setSearchParams, 'search', value, true),
        setStatus: (value) => setParam(setSearchParams, 'status', value === 'All' ? '' : value),
      }),
    [search, setSearchParams, status],
  );

  const setSearch = useCallback(
    (value: string) => setParam(setSearchParams, 'search', value, true),
    [setSearchParams],
  );
  const setStatus = useCallback(
    (value: string) => setParam(setSearchParams, 'status', value === 'All' ? '' : value),
    [setSearchParams],
  );
  const openRecord = useCallback(
    (id: string) => navigate(withSearch(`${config.route}/${id}`, searchParams)),
    [config.route, navigate, searchParams],
  );
  const closeRecord = useCallback(
    () => navigate(withSearch(config.route, searchParams)),
    [config.route, navigate, searchParams],
  );
  const goToRecordAt = useCallback(
    (index: number) => {
      const record = records[index];
      if (record) navigate(withSearch(`${config.route}/${record.id}`, searchParams));
    },
    [config.route, navigate, records, searchParams],
  );
  const goToPreviousRecord = useCallback(() => goToRecordAt(selectedIndex - 1), [
    goToRecordAt,
    selectedIndex,
  ]);
  const goToNextRecord = useCallback(() => goToRecordAt(selectedIndex + 1), [
    goToRecordAt,
    selectedIndex,
  ]);
  const aliceRecordType = MANAGED_TABLE_ALICE_RECORD_TYPES[tableKey];
  const aliceContext = useMemo(
    () =>
      pageContext({
        portalKey: adminConfig.appId,
        pageKey: tableKey,
        pageTitle: page.title,
        recordType: recordId ? aliceRecordType : undefined,
        recordId,
        recordLabel: selectedRecord
          ? plainValue(selectedRecord.fields[config.titleField]) ?? selectedRecord.id
          : recordId,
        organizationLabel: selectedRecord && config.subtitleField
          ? plainValue(selectedRecord.fields[config.subtitleField])
          : undefined,
        contextItems: [
          contextItem(
            `${tableKey}-page`,
            'page',
            page.title,
            `${config.group} records from ${config.tableName}.`,
          ),
          ...(recordId
            ? [
                contextItem(
                  `${tableKey}-${recordId}`,
                  aliceRecordType,
                  selectedRecord
                    ? plainValue(selectedRecord.fields[config.titleField]) ?? selectedRecord.id
                    : recordId,
                  'Read-only managed table record selected in the center workspace.',
                ),
              ]
            : []),
        ],
        suggestedActions: recordId
          ? [
              suggestion(
                `${tableKey}-summarize`,
                config.group === 'Billing' ? 'Prepare billing summary' : 'Summarize AI record',
                `Use visible ${config.title.toLowerCase()} fields to prepare a safe summary.`,
              ),
              suggestion(
                `${tableKey}-draft`,
                config.group === 'Billing' ? 'Draft client note' : 'Draft internal note',
                'Start a Composer draft from this record context.',
                'composer',
              ),
            ]
          : [],
      }),
    [aliceRecordType, config, page.title, recordId, selectedRecord, tableKey],
  );
  useAlicePageContext(aliceContext);

  if (query.isLoading) return <ManagementLoadingState metricCount={3} />;
  if (query.error) return <ManagementErrorState title={`Couldn't load ${config.title.toLowerCase()}`} error={query.error} />;

  return (
    <ManagementPage>
      <ManagementHeader
        title={page.title}
        description={page.description}
        source={page.source}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: `Search ${config.title.toLowerCase()}`,
        }}
        filters={
          config.statusField
            ? [
                {
                  id: 'status',
                  label: 'Status',
                  value: status,
                  onChange: setStatus,
                  options: statusOptions,
                },
              ]
            : []
        }
        activeFilters={activeFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <WorkspaceSplitView
        footer={
          <ManagementFooterSummary>
            Showing <span className="tabular-nums text-foreground">{records.length}</span>
            {' '}loaded {records.length === 1 ? 'record' : 'records'}
          </ManagementFooterSummary>
        }
        detail={
          recordId && (
            <ManagedTableDetailsPanel
              tableKey={tableKey}
              recordId={recordId}
              listTitle={config.title}
              selectedIndex={selectedIndex}
              totalRecords={records.length}
              onClose={closeRecord}
              onPrevious={goToPreviousRecord}
              onNext={goToNextRecord}
            />
          )
        }
      >
        {recordId ? (
          <ManagedTableCompactList
            config={config}
            records={records}
            activeRecordId={recordId}
            onOpenRecord={openRecord}
          />
        ) : viewMode === 'cards' ? (
          <ManagedTableCards
            config={config}
            records={records}
            activeRecordId={recordId}
            onOpenRecord={openRecord}
          />
        ) : (
          <ManagedTableRows
            config={config}
            records={records}
            activeRecordId={recordId}
            onOpenRecord={openRecord}
          />
        )}
      </WorkspaceSplitView>
    </ManagementPage>
  );
}

const MANAGED_TABLE_ALICE_RECORD_TYPES: Record<ManagedTableKey, AliceContextSourceType> = {
  forms: 'manual',
  formEntries: 'manual',
  timeEntries: 'time_entry',
  invoices: 'invoice',
  subscriptions: 'subscription',
  payments: 'payment',
  agents: 'agent',
  calls: 'call',
  prompts: 'prompt',
};

function ManagedTableRows({
  config,
  records,
  activeRecordId,
  onOpenRecord,
}: {
  config: ManagedTableConfig;
  records: ManagedTableRecord[];
  activeRecordId?: string;
  onOpenRecord: (id: string) => void;
}) {
  const columns = config.columns.filter((column) => column.table !== false);

  return (
    <ManagementTable>
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.id}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.length === 0 && (
            <tr>
              <td colSpan={columns.length}>
                <ManagementEmptyState
                  icon={Database}
                  title={`No ${config.title.toLowerCase()} match this view.`}
                  body="Try clearing search or status filters."
                />
              </td>
            </tr>
          )}
          {records.map((record) => (
            <tr
              key={record.id}
              onClick={() => onOpenRecord(record.id)}
              tabIndex={0}
              role="button"
              className={cn(
                'cursor-pointer focus-visible:bg-muted/40 focus-visible:outline-none',
                activeRecordId === record.id && 'bg-primary/5',
              )}
            >
              {columns.map((column) => (
                <td key={column.id}>{renderCell(record.fields[column.sourceField], column)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </ManagementTable>
  );
}

function ManagedTableCards({
  config,
  records,
  activeRecordId,
  onOpenRecord,
}: {
  config: ManagedTableConfig;
  records: ManagedTableRecord[];
  activeRecordId?: string;
  onOpenRecord: (id: string) => void;
}) {
  if (records.length === 0) {
    return (
      <Card>
        <ManagementEmptyState
          icon={Database}
          title={`No ${config.title.toLowerCase()} match this view.`}
          body="Try clearing search or status filters."
        />
      </Card>
    );
  }

  return (
    <ManagementCardGrid>
      {records.map((record) => {
        const title = plainValue(record.fields[config.titleField]) || record.id;
        const subtitle = config.subtitleField ? plainValue(record.fields[config.subtitleField]) : undefined;
        const status = config.statusField ? plainValue(record.fields[config.statusField]) : undefined;
        const mediaColumn = getMediaColumn(config);
        const mediaUrl = mediaColumn ? imageUrlFromValue(record.fields[mediaColumn.sourceField]) : undefined;
        const mediaFitClass =
          config.key === 'agents' || config.key === 'calls'
            ? 'object-contain p-2'
            : 'object-cover object-top';
        const previewColumns = config.columns
          .filter(
            (column) =>
              column.card !== false &&
              column.sourceField !== config.titleField &&
              !['media', 'title', 'subtitle', 'badge', 'hidden'].includes(column.cardRole ?? ''),
          )
          .slice(0, 4);

        return (
          <ManagementRecordCard
            key={record.id}
            active={activeRecordId === record.id}
            onClick={() => onOpenRecord(record.id)}
            role="button"
            tabIndex={0}
            className="cursor-pointer overflow-hidden"
          >
            {mediaUrl && (
              <div className="-mx-3 -mt-3 mb-3 aspect-[16/9] overflow-hidden border-b border-border bg-muted">
                <img src={mediaUrl} alt="" className={cn('h-full w-full', mediaFitClass)} />
              </div>
            )}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2.5">
                  {!mediaUrl && (
                    <CompactListMediaFallback label={title} className="h-10 w-10" />
                  )}
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-foreground">{title}</div>
                    {subtitle && <div className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</div>}
                  </div>
                </div>
                {status && <ManagementStatusBadge>{status}</ManagementStatusBadge>}
              </div>
              <div className="grid gap-2 text-xs text-muted-foreground">
                {previewColumns.map((column) => (
                  <div key={column.id} className="flex min-w-0 items-center justify-between gap-3">
                    <span className="shrink-0 font-medium">{column.label}</span>
                    <span className="min-w-0 truncate text-foreground">
                      {formatCardValue(record.fields[column.sourceField], column) || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </ManagementRecordCard>
        );
      })}
    </ManagementCardGrid>
  );
}

function ManagedTableCompactList({
  config,
  records,
  activeRecordId,
  onOpenRecord,
}: {
  config: ManagedTableConfig;
  records: ManagedTableRecord[];
  activeRecordId?: string;
  onOpenRecord: (id: string) => void;
}) {
  if (records.length === 0) {
    return (
      <Card>
        <ManagementEmptyState
          icon={Database}
          title={`No ${config.title.toLowerCase()} match this view.`}
          body="Try clearing search or status filters."
        />
      </Card>
    );
  }

  const mediaColumn = getMediaColumn(config);
  const metaColumns = config.columns
    .filter(
      (column) =>
        column.sourceField !== config.titleField &&
        column.cardRole !== 'media' &&
        column.cardRole !== 'title' &&
        column.cardRole !== 'subtitle' &&
        column.cardRole !== 'hidden',
    )
    .slice(0, 2);

  return (
    <CompactRecordList title={config.title}>
      {records.map((record) => {
        const title = plainValue(record.fields[config.titleField]) || record.id;
        const subtitle = config.subtitleField ? plainValue(record.fields[config.subtitleField]) : undefined;
        const mediaUrl = mediaColumn ? imageUrlFromValue(record.fields[mediaColumn.sourceField]) : undefined;
        const mediaFitClass =
          config.key === 'agents' || config.key === 'calls'
            ? 'object-contain p-1'
            : 'object-cover object-top';

        return (
          <CompactRecordListItem
            key={record.id}
            selected={activeRecordId === record.id}
            onSelect={() => onOpenRecord(record.id)}
            media={
              mediaUrl ? (
                <img
                  src={mediaUrl}
                  alt=""
                  className={cn(
                    'mt-0.5 h-10 w-12 flex-shrink-0 rounded-md border border-border/70 bg-muted',
                    mediaFitClass,
                  )}
                />
              ) : (
                <CompactListMediaFallback label={title} className="mt-0.5 h-10 w-10" />
              )
            }
            title={title}
            subtitle={subtitle}
            meta={metaColumns.map((column) => {
              const value = formatCardValue(record.fields[column.sourceField], column);
              if (!value) return null;
              return (
                <span key={column.id} className="max-w-full truncate">
                  {column.label}: {value}
                </span>
              );
            })}
          />
        );
      })}
    </CompactRecordList>
  );
}

function ManagedTableDetailsPanel({
  tableKey,
  recordId,
  listTitle,
  selectedIndex,
  totalRecords,
  onClose,
  onPrevious,
  onNext,
}: {
  tableKey: ManagedTableKey;
  recordId: string;
  listTitle: string;
  selectedIndex: number;
  totalRecords: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <ManagedTableDetailsSurface
      tableKey={tableKey}
      recordId={recordId}
      listTitle={listTitle}
      selectedIndex={selectedIndex}
      totalRecords={totalRecords}
      onClose={onClose}
      onPrevious={onPrevious}
      onNext={onNext}
    />
  );
}

function ManagedTableDetailsSurface({
  tableKey,
  recordId,
  listTitle,
  selectedIndex,
  totalRecords,
  onClose,
  onPrevious,
  onNext,
}: {
  tableKey: ManagedTableKey;
  recordId: string;
  listTitle?: string;
  selectedIndex?: number;
  totalRecords?: number;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const config = MANAGED_TABLE_CONFIGS[tableKey];
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const editableFields = useMemo(() => config.editableFields ?? [], [config]);
  const canEdit = editableFields.length > 0;
  const canDelete = Boolean(config.deletable);
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, EditableFormValue>>({});

  const query = useQuery({
    queryKey: ['managed-table', tableKey, 'detail', recordId],
    queryFn: () => managedTablesApi.get(tableKey, recordId),
  });
  const item = query.data?.item;

  // Switching records always drops out of edit mode.
  useEffect(() => {
    setIsEditing(false);
  }, [recordId]);

  const patchMutation = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      managedTablesApi.patch(tableKey, recordId, fields),
    onSuccess: (data) => {
      queryClient.setQueryData(['managed-table', tableKey, 'detail', recordId], data);
      void queryClient.invalidateQueries({ queryKey: ['managed-table', tableKey] });
      if (tableKey === 'timeEntries') {
        void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      }
      setIsEditing(false);
      setNotice(`${config.title} record updated`);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Save failed', 'destructive'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => managedTablesApi.delete(tableKey, recordId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['managed-table', tableKey] });
      if (tableKey === 'timeEntries') {
        void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      }
      setNotice(`${config.title} record deleted`);
      onClose();
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Delete failed', 'destructive'),
  });

  const title = item ? plainValue(item.fields[config.titleField]) || item.id : config.title;
  const status = item && config.statusField ? plainValue(item.fields[config.statusField]) : undefined;
  const subtitle = item && config.subtitleField ? plainValue(item.fields[config.subtitleField]) : undefined;

  const handleEditToggle = () => {
    if (!item) return;
    if (isEditing) {
      setIsEditing(false);
      return;
    }
    setEditValues(buildEditableValues(item, editableFields));
    setIsEditing(true);
  };
  const handleSave = () => patchMutation.mutate(editValues);
  const handleDelete = () => {
    if (deleteMutation.isPending) return;
    const confirmed = window.confirm(
      `Delete this ${config.title.toLowerCase()} record? This permanently removes it from ${config.tableName}.`,
    );
    if (confirmed) deleteMutation.mutate();
  };

  const description = (
    <>
      <Badge variant="outline">{config.tableName}</Badge>
      {status && <Badge variant="muted">{status}</Badge>}
      {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
    </>
  );
  const body = (
    <>
      {query.isLoading && <DetailsDrawerSkeleton />}
      {query.error && (
        <DetailsDrawerError
          error={query.error}
          fallback={`Could not load ${config.title.toLowerCase()} record.`}
        />
      )}
      {item && (
        <ManagedTableDetailsBody
          item={item}
          config={config}
          status={status}
          isEditing={isEditing}
          editableFields={editableFields}
          editValues={editValues}
          onEditChange={(sourceField, value) =>
            setEditValues((prev) => ({ ...prev, [sourceField]: value }))
          }
          saveError={patchMutation.error}
        />
      )}
    </>
  );

  return (
    <RecordDetailShell
      title={title}
      recordType={listTitle}
      onClose={onClose}
      canGoPrevious={typeof selectedIndex === 'number' && selectedIndex > 0}
      canGoNext={
        typeof selectedIndex === 'number' &&
        typeof totalRecords === 'number' &&
        selectedIndex >= 0 &&
        selectedIndex < totalRecords - 1
      }
      onPrevious={onPrevious}
      onNext={onNext}
      isEditing={isEditing}
      onEdit={item && canEdit ? handleEditToggle : undefined}
      onSave={item && canEdit ? handleSave : undefined}
      savePending={patchMutation.isPending}
      onDelete={item && canDelete ? handleDelete : undefined}
      deletePending={deleteMutation.isPending}
      className="2xl:sticky 2xl:top-0"
    >
      <RecordDetailHeading icon={Database} title={title} badges={description} />
      {body}
    </RecordDetailShell>
  );
}

function ManagedTableDetailsBody({
  item,
  config,
  status,
  isEditing,
  editableFields,
  editValues,
  onEditChange,
  saveError,
}: {
  item: ManagedTableRecord;
  config: ManagedTableConfig;
  status?: string;
  isEditing: boolean;
  editableFields: ManagedTableEditableField[];
  editValues: Record<string, EditableFormValue>;
  onEditChange: (sourceField: string, value: EditableFormValue) => void;
  saveError: Error | null;
}) {
  const groupedColumns = groupDetailColumns(config.columns).filter(([, columns]) =>
    columns.some((column) => hasDetailValue(item.fields[column.sourceField], column)),
  );

  return (
    <DetailsPanelBody className="space-y-5">
      <DetailFactGrid>
        <DetailFact label="Record ID" value={item.id} />
        <DetailFact label="Created" value={formatDateTime(item.createdTime)} />
        <DetailFact label="Source" value={config.tableName} />
        <DetailFact label="Status" value={status} />
      </DetailFactGrid>

      {isEditing && editableFields.length > 0 && (
        <DetailGroup title={`Edit ${config.title}`}>
          <div className="space-y-3">
            {editableFields.map((field) => (
              <ManagedTableEditableInput
                key={field.sourceField}
                field={field}
                value={editValues[field.sourceField]}
                onChange={(value) => onEditChange(field.sourceField, value)}
              />
            ))}
            {saveError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
                {saveError.message}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              Use Save in the header to apply changes, or Cancel to discard.
            </p>
          </div>
        </DetailGroup>
      )}

      {groupedColumns.map(([group, columns]) => (
        <DetailGroup key={group} title={group}>
          <div className="space-y-2">
            {columns.map((column) => (
              <DetailRow
                key={column.id}
                label={column.label}
                value={renderDetailValue(item.fields[column.sourceField], column)}
              />
            ))}
          </div>
        </DetailGroup>
      ))}

      <details className="rounded-lg border border-border/70 bg-muted/20">
        <summary className="cursor-pointer select-none list-none px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          All fields
        </summary>
        <div className="space-y-2 border-t border-border/70 px-3 py-3">
          {Object.entries(item.fields)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([field, value]) => (
              <DetailRow key={field} label={field} value={renderRawValue(value)} />
            ))}
        </div>
      </details>
    </DetailsPanelBody>
  );
}

type EditableFormValue = string | number | boolean | string[] | null;

function ManagedTableEditableInput({
  field,
  value,
  onChange,
}: {
  field: ManagedTableEditableField;
  value: EditableFormValue | undefined;
  onChange: (value: EditableFormValue) => void;
}) {
  const baseClass =
    'w-full rounded-md border border-input bg-background px-3 py-2 text-[13px] outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15';

  return (
    <label className="block space-y-1.5 text-[12px] font-medium text-foreground">
      <span>{field.label}</span>
      {field.type === 'longText' ? (
        <textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          rows={4}
          placeholder={field.placeholder}
          className={cn(baseClass, 'resize-y')}
        />
      ) : field.type === 'boolean' ? (
        <span className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => onChange(event.target.checked)}
            className="h-4 w-4 rounded border-input accent-primary"
          />
          <span className="text-[13px] font-normal text-muted-foreground">
            {field.placeholder ?? `Enable ${field.label.toLowerCase()}`}
          </span>
        </span>
      ) : field.type === 'number' ? (
        <input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(event) => onChange(Number(event.target.value))}
          className={baseClass}
        />
      ) : field.type === 'linkedRecord' ? (
        <>
          <input
            type="text"
            value={Array.isArray(value) ? value.join(', ') : ''}
            onChange={(event) => onChange(parseLinkedRecordInput(event.target.value))}
            placeholder={field.placeholder}
            className={baseClass}
          />
          <span className="block text-[11px] font-normal text-muted-foreground">
            Use Airtable record ids. Separate multiple records with commas.
          </span>
        </>
      ) : (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder}
          className={baseClass}
        />
      )}
    </label>
  );
}

function buildEditableValues(
  item: ManagedTableRecord,
  fields: ManagedTableEditableField[],
): Record<string, EditableFormValue> {
  const values: Record<string, EditableFormValue> = {};
  for (const field of fields) {
    const value = item.fields[field.sourceField];
    if (field.type === 'boolean') values[field.sourceField] = Boolean(value);
    else if (field.type === 'number') values[field.sourceField] = Number(plainValue(value) ?? 0);
    else if (field.type === 'linkedRecord') values[field.sourceField] = toLinkedRecordIds(value);
    else values[field.sourceField] = plainValue(value) ?? '';
  }
  return values;
}

function toLinkedRecordIds(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  const text = plainValue(value);
  return text ? parseLinkedRecordInput(text) : [];
}

function parseLinkedRecordInput(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function groupDetailColumns(columns: ManagedTableColumn[]) {
  const groups = new Map<string, ManagedTableColumn[]>();
  for (const column of columns) {
    if (column.detail === false) continue;
    const group = column.detailGroup ?? 'Overview';
    groups.set(group, [...(groups.get(group) ?? []), column]);
  }

  const preferredOrder = [
    'Overview',
    'Contact/Client',
    'Status',
    'Performance/Usage',
    'Media',
    'Prompt/Transcript/Summary',
    'Raw Fields',
  ];

  return Array.from(groups.entries()).sort(
    ([a], [b]) => preferredOrder.indexOf(a) - preferredOrder.indexOf(b),
  );
}

function buildStatusOptions(
  records: ManagedTableRecord[],
  config: ManagedTableConfig,
  currentStatus: string,
): ManagementFilterOption[] {
  const counts = new Map<string, number>();
  if (config.statusField) {
    for (const record of records) {
      const value = plainValue(record.fields[config.statusField]);
      if (!value) continue;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  if (currentStatus !== 'All' && !counts.has(currentStatus)) counts.set(currentStatus, 0);
  return [
    { value: 'All', label: 'All', count: records.length },
    ...Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([value, count]) => ({ value, label: value, count })),
  ];
}

function buildActiveFilters({
  search,
  status,
  setSearch,
  setStatus,
}: {
  search: string;
  status: string;
  setSearch: (value: string) => void;
  setStatus: (value: string) => void;
}): ManagementActiveFilter[] {
  const filters: ManagementActiveFilter[] = [];
  if (status !== 'All') {
    filters.push({ id: 'status', label: 'Status', value: status, onClear: () => setStatus('All') });
  }
  if (search) {
    filters.push({ id: 'search', label: 'Search', value: `"${search}"`, onClear: () => setSearch('') });
  }
  return filters;
}

function setParam(
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  key: string,
  value: string,
  replace = false,
) {
  setSearchParams(
    (prev) => {
      const params = new URLSearchParams(prev);
      if (value) params.set(key, value);
      else params.delete(key);
      return params;
    },
    { replace },
  );
}

function withSearch(path: string, searchParams: URLSearchParams) {
  const qs = searchParams.toString();
  return qs ? `${path}?${qs}` : path;
}

function renderCell(value: unknown, column: ManagedTableColumn) {
  const text = plainValue(value);
  if (column.type === 'image') {
    const url = imageUrlFromValue(value);
    return url ? (
      <img src={url} alt="" className="h-9 w-12 rounded-md border border-border/70 object-cover object-top" />
    ) : (
      <span className="text-muted-foreground">-</span>
    );
  }
  if (!text) return <span className="text-muted-foreground">-</span>;
  if (column.type === 'status') return <ManagementStatusBadge>{text}</ManagementStatusBadge>;
  if (column.type === 'boolean') return <Badge variant={text === 'true' ? 'success' : 'muted'}>{text === 'true' ? 'Yes' : 'No'}</Badge>;
  if (column.type === 'url') return <UrlValue value={text} />;
  if (column.type === 'audioUrl') return <UrlValue value={text} label="Recording" />;
  if (column.type === 'attachment') return <AttachmentLink value={value} />;
  if (column.type === 'phone') return <ContactValue value={text} kind="phone" />;
  if (column.type === 'email') return <ContactValue value={text} kind="email" />;
  if (column.type === 'rating') return <RatingValue value={value} compact />;
  if (column.type === 'duration') return <span className="tabular-nums">{formatDurationMinutes(value)}</span>;
  if (column.type === 'currency') return <span className="tabular-nums">{formatCurrency(value, column)}</span>;
  if (column.type === 'date') return <span>{formatDate(text)}</span>;
  if (column.type === 'datetime') return <span>{formatDateTime(text)}</span>;
  if (column.type === 'number') return <span className="tabular-nums">{formatNumber(value)}</span>;
  if (column.type === 'longText' || column.type === 'richText') return <span className="block max-w-[360px] truncate">{text}</span>;
  return <span>{text}</span>;
}

function renderDetailValue(value: unknown, column: ManagedTableColumn) {
  const text = plainValue(value);
  if (column.type === 'image') {
    const url = imageUrlFromValue(value);
    return url ? <ImageValue url={url} /> : undefined;
  }
  if (column.type === 'attachment') {
    const links = attachmentLinks(value);
    return links.length > 0 ? <AttachmentList links={links} /> : undefined;
  }
  if (!text) return undefined;
  if (column.type === 'status') return <ManagementStatusBadge>{text}</ManagementStatusBadge>;
  if (column.type === 'boolean') return text === 'true' ? 'Yes' : 'No';
  if (column.type === 'url') return <UrlValue value={text} />;
  if (column.type === 'audioUrl') return <AudioValue value={text} />;
  if (column.type === 'phone') return <ContactValue value={text} kind="phone" />;
  if (column.type === 'email') return <ContactValue value={text} kind="email" />;
  if (column.type === 'rating') return <RatingValue value={value} />;
  if (column.type === 'duration') return formatDurationMinutes(value);
  if (column.type === 'currency') return formatCurrency(value, column);
  if (column.type === 'date') return formatDate(text);
  if (column.type === 'datetime') return formatDateTime(text);
  if (column.type === 'number') return formatNumber(value);
  if (column.type === 'longText' || column.type === 'richText') return <LongTextValue value={text} />;
  return renderRawValue(value);
}

function hasDetailValue(value: unknown, column: ManagedTableColumn) {
  if (column.type === 'image') return Boolean(imageUrlFromValue(value));
  if (column.type === 'attachment') return attachmentLinks(value).length > 0;
  return Boolean(plainValue(value));
}

function renderRawValue(value: unknown) {
  if (value === undefined || value === null || value === '') return undefined;
  if (Array.isArray(value) && value.length === 0) return undefined;
  if (isPrimitiveList(value)) return value.map(String).join(', ');
  const text = plainValue(value);
  if (text && typeof value !== 'object') return text;
  if (typeof value === 'object') return <JsonPreview value={value} />;
  return String(value);
}

function UrlValue({ value, label = 'Open' }: { value: string; label?: string }) {
  const href = normalizeUrl(value);
  if (!href) return <span>{value}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-primary underline-offset-2 hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </a>
  );
}

function ContactValue({ value, kind }: { value: string; kind: 'phone' | 'email' }) {
  const href = kind === 'phone' ? `tel:${value.replace(/[^\d+]/g, '')}` : `mailto:${value}`;
  return (
    <a
      href={href}
      className="font-medium text-primary underline-offset-2 hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {value}
    </a>
  );
}

function AttachmentLink({ value }: { value: unknown }) {
  const link = attachmentLinks(value)[0];
  if (!link) return <span className="text-muted-foreground">-</span>;
  return <UrlValue value={link.url} label={link.name ?? 'Attachment'} />;
}

function AttachmentList({ links }: { links: Array<{ url: string; name?: string }> }) {
  return (
    <div className="flex flex-wrap gap-2">
      {links.map((link) => (
        <UrlValue key={link.url} value={link.url} label={link.name ?? 'Attachment'} />
      ))}
    </div>
  );
}

function AudioValue({ value }: { value: string }) {
  const href = normalizeUrl(value);
  if (!href) return <span>{value}</span>;
  return (
    <div className="space-y-2">
      <audio controls src={href} className="w-full max-w-md" />
      <UrlValue value={href} label="Open recording" />
    </div>
  );
}

function ImageValue({ url }: { url: string }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
      <img
        src={url}
        alt=""
        className="max-h-[280px] w-full rounded-md border border-border bg-muted/40 object-contain"
      />
    </a>
  );
}

function LongTextValue({ value }: { value: string }) {
  return <div className="whitespace-pre-wrap rounded-md bg-muted/30 px-3 py-2 text-[13px] leading-relaxed">{value}</div>;
}

function RatingValue({ value, compact = false }: { value: unknown; compact?: boolean }) {
  const number = typeof value === 'number' ? value : Number(plainValue(value));
  if (!Number.isFinite(number)) return <span>{plainValue(value) ?? '-'}</span>;
  const label = `${number}/5`;
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <span className="font-medium text-primary">{label}</span>
      {!compact && <span className="text-muted-foreground">rating</span>}
    </span>
  );
}

function getMediaColumn(config: ManagedTableConfig) {
  return (
    config.columns.find((column) => column.cardRole === 'media') ??
    config.columns.find((column) => column.type === 'image')
  );
}

function formatCardValue(value: unknown, column: ManagedTableColumn) {
  if (column.type === 'currency') return formatCurrency(value, column);
  if (column.type === 'date') return formatDate(plainValue(value) ?? '');
  if (column.type === 'datetime') return formatDateTime(plainValue(value) ?? '');
  if (column.type === 'duration') return formatDurationMinutes(value);
  if (column.type === 'rating') return plainValue(value) ? `${plainValue(value)}/5` : undefined;
  if (column.type === 'boolean') return plainValue(value) === 'true' ? 'Yes' : 'No';
  if (column.type === 'attachment' || column.type === 'audioUrl') {
    return attachmentLinks(value)[0]?.name ?? (plainValue(value) ? 'Attached' : undefined);
  }
  return plainValue(value);
}

function plainValue(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const values = value.map(plainValue).filter((item): item is string => Boolean(item));
    return values.length ? values.join(', ') : undefined;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      plainValue(record.name) ??
      plainValue(record.value) ??
      plainValue(record.label) ??
      plainValue(record.filename) ??
      plainValue(record.url)
    );
  }
  return undefined;
}

function imageUrlFromValue(value: unknown): string | undefined {
  if (typeof value === 'string') return normalizeUrl(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const url = imageUrlFromValue(item);
      if (url) return url;
    }
    return undefined;
  }
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const thumbnails = record.thumbnails as Record<string, unknown> | undefined;
  const large = thumbnails?.large as Record<string, unknown> | undefined;
  const full = thumbnails?.full as Record<string, unknown> | undefined;
  return (
    imageUrlFromValue(large?.url) ??
    imageUrlFromValue(full?.url) ??
    imageUrlFromValue(record.url) ??
    imageUrlFromValue(record.value)
  );
}

function attachmentLinks(value: unknown): Array<{ url: string; name?: string }> {
  if (typeof value === 'string') {
    const url = normalizeUrl(value);
    return url ? [{ url }] : [];
  }
  if (Array.isArray(value)) return value.flatMap(attachmentLinks);
  if (!value || typeof value !== 'object') return [];
  const record = value as Record<string, unknown>;
  const url = normalizeUrl(plainValue(record.url));
  if (!url) return attachmentLinks(record.value);
  return [
    {
      url,
      name: plainValue(record.filename) ?? plainValue(record.name),
    },
  ];
}

function normalizeUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return undefined;
}

function isPrimitiveList(value: unknown): value is Array<string | number | boolean> {
  return Array.isArray(value) && value.every((item) => ['string', 'number', 'boolean'].includes(typeof item));
}

function formatCurrency(value: unknown, column?: ManagedTableColumn) {
  const number = typeof value === 'number' ? value : Number(plainValue(value));
  if (!Number.isFinite(number)) return plainValue(value) ?? '-';
  const dollars = column?.currencyUnit === 'cents' ? number / 100 : number;
  return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(value: unknown) {
  const number = typeof value === 'number' ? value : Number(plainValue(value));
  if (!Number.isFinite(number)) return plainValue(value) ?? '-';
  return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatDurationMinutes(value: unknown) {
  const number = typeof value === 'number' ? value : Number(plainValue(value));
  if (!Number.isFinite(number)) return plainValue(value) ?? '-';
  const minutes = Math.round(number);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours <= 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}
